/* eslint-disable no-console */
import { Component, Inject, OnDestroy } from '@angular/core'
import { Account } from '@helios/generic-components'
import {
  AccountRowUpdateEvent,
  EventType,
  GlobalMessagingService,
  HeliosMessageType,
} from '@helios/messaging'
import { BasePanelComponent, LayoutContainer } from '@helios/panel-base'
import { SessionsService } from '@helios/sessions'
import {
  focusPanelAction,
  PeriodLength,
  periodLengthToDate,
  RootState,
  selectActiveSession,
  selectActualReconDate,
  selectCurrentUser,
  selectFocusedApprovalsAccountId,
  setFocusedAccounts,
  setFocusedCommentaryAccountId,
  setFocusedTotalAccountBreaks,
} from '@helios/state-management'
import { Store } from '@ngrx/store'
import { formatDate, ISODateFormat } from '@northfork/basics'
import { ComponentContainer } from 'golden-layout'
import { Observable } from 'rxjs'
import { distinctUntilChanged, filter, map, take } from 'rxjs/operators'
import { AccountListKey } from '../panel-key'

const AccountListJobsFilterKey = `${AccountListKey}/jobs`

@Component({
  selector: 'hs-account-list',
  templateUrl: './account-list-panel.component.html',
  styleUrls: ['./account-list-panel.component.scss'],
})
export class AccountListPanelComponent
  extends BasePanelComponent
  implements OnDestroy
{
  /** @internal */
  session = this.store.select(selectActiveSession)
  currentUser = this.store.select(selectCurrentUser).pipe(
    filter(user => user != null),
    distinctUntilChanged((a, b) => a?.auth0Id === b?.auth0Id),
  )

  readonly updatedAccount: Observable<number[]> = this.messagingService
    .on<unknown, AccountRowUpdateEvent<Account>>(EventType.MESSAGE, [
      HeliosMessageType.AccountCreate,
      HeliosMessageType.AccountUpdate,
      HeliosMessageType.AccountDelete,
    ])
    .pipe(
      map(e => {
        if (Array.isArray(e.payload)) {
          return e.payload
        }
        return [e.payload.accountId]
      }),
    )

  readonly actualReconDate = this.store
    .select(selectActualReconDate)
    .pipe(
      map(date => formatDate(date, ISODateFormat)),
      take(1),
    )
    .toPromise()

  approvalsAccountList?: number[]

  constructor(
    @Inject(LayoutContainer) container: ComponentContainer,
    private readonly store: Store<RootState>,
    private readonly sessionsService: SessionsService,
    private readonly messagingService: GlobalMessagingService,
  ) {
    super(container)
  }

  ngOnInit(): void {
    this.store
      .select(selectFocusedApprovalsAccountId)
      .pipe(distinctUntilChanged())
      .subscribe(result => {
        this.approvalsAccountList = result
      })
  }
  ngOnDestroy() {
    this.setSelectedAccounts([])
    this.messagingService.deleteFilters(AccountListKey)
    this.messagingService.deleteFilters(AccountListJobsFilterKey)
  }

  /** @internal */
  setSelectedAccounts(accountIds: number[]) {
    this.store.dispatch(setFocusedAccounts({ accountIds }))
  }

  /**
   * DLITE-4110: drive the comments drawer from the selected account without
   * requiring a session. `accountId` is null when the selection isn't
   * commentary-eligible (nothing/multiple/aggregate), which leaves the drawer's
   * current account untouched.
   */
  setCommentaryAccount(accountId: number | null) {
    this.store.dispatch(setFocusedCommentaryAccountId({ accountId }))
  }

  setSelectedTotalBreaks(totalBreaks: number) {
    this.store.dispatch(setFocusedTotalAccountBreaks({ totalBreaks }))
  }

  focusExternalFilePanel(panelKey: string) {
    this.store.dispatch(focusPanelAction(panelKey))
  }

  accountsInViewChanged(accounts: number[]) {
    this.messagingService.setFilters(AccountListKey, [
      {
        jsonPath: '/payload/accountId',
        values: accounts.map(a => a.toString()),
      },
    ])
    this.messagingService.setFilters(AccountListJobsFilterKey, [
      {
        jsonPath: '/payload/affectedAccountIds',
        values: accounts.map(a => a.toString()),
      },
    ])
  }

  /** @internal */
  async setLoadedAccount({
    accountIds,
    reconDate,
    withModal,
    isLIBAccount,
  }: {
    accountIds: number[]
    reconDate: Date
    withModal: boolean
    isLIBAccount: boolean
  }) {
    if (withModal) this.sessionsService.createFromModal(accountIds, reconDate)
    else {
      this.sessionsService.create(
        accountIds,
        reconDate,
        periodLengthToDate(PeriodLength.Last100Days),
        false,
        undefined,
        isLIBAccount,
      )
    }
  }

  /** @internal */
  loadAccountWithTimePeriod({
    accountIds,
    reconDate,
    periodLength,
    isLIBAccount,
  }: {
    accountIds: number[]
    reconDate: Date
    periodLength: PeriodLength
    isLIBAccount: boolean
  }) {
    const isPeriodLengthFromDate = periodLength == PeriodLength.FromDate
    if (isPeriodLengthFromDate) {
      this.sessionsService.createFromModal(
        accountIds,
        reconDate,
        isPeriodLengthFromDate,
        isLIBAccount,
      )
    } else {
      this.sessionsService.create(
        accountIds,
        reconDate,
        periodLengthToDate(periodLength),
        false,
        undefined,
        isLIBAccount,
      )
    }
  }
}
